import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';

import { SnRouterHelperService, mapToRouteDataItem } from '@lib/state/router';

import { ISnLayoutAppbarConfig } from '../interfaces';
import { SnLayoutAppbarConfig } from '../models';
import { SnLayoutAppbarMode } from '../types';
import { SnLayoutAppbarActionsReference } from './layout-appbar-actions.reference';
import { SnLayoutAppbarNavButtonReference } from './layout-appbar-nav-button.reference';
import { SnLayoutAppbarSearchReference } from './layout-appbar-search.reference';

export class SnLayoutAppbarReference {
  public readonly search: SnLayoutAppbarSearchReference;
  public readonly actions: SnLayoutAppbarActionsReference;
  public readonly navButton: SnLayoutAppbarNavButtonReference;

  private readonly _visibleChange$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private readonly _modeChange$: BehaviorSubject<SnLayoutAppbarMode> = new BehaviorSubject(
    'standart',
  );
  private readonly _classChange$: BehaviorSubject<string> = new BehaviorSubject('');
  private readonly _destroy$: Subject<void> = new Subject();

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _routerHelper: SnRouterHelperService,
    private readonly _config: SnLayoutAppbarConfig,
  ) {
    this.search = new SnLayoutAppbarSearchReference();
    this.actions = new SnLayoutAppbarActionsReference();
    this.navButton = new SnLayoutAppbarNavButtonReference();

    this._watchRouter();
  }

  get visibleChange$(): Observable<boolean> {
    return this._visibleChange$.asObservable();
  }

  get modeChange$(): Observable<SnLayoutAppbarMode> {
    return this._modeChange$.asObservable();
  }

  get classChange$(): Observable<string> {
    return this._classChange$.asObservable();
  }

  get isVisible(): boolean {
    return this._visibleChange$.value;
  }

  get mode(): SnLayoutAppbarMode {
    return this._modeChange$.value;
  }

  public onDestroy(): void {
    this.search.onDestroy();
    this.actions.onDestroy();
    this.navButton.onDestroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public show(): void {
    this.toggleVisibility(true);
  }

  public hide(): void {
    this.toggleVisibility(false);
  }

  public isCollapsible(): boolean {
    return this._config.collapsible;
  }

  public toggleVisibility(visible: boolean = !this.isVisible): void {
    if (this.isVisible !== visible) {
      this._config.update({ hidden: !visible });
      this._visibleChange$.next(visible);
    }
  }

  public setMode(mode: SnLayoutAppbarMode): void {
    if (this.mode !== mode) {
      this._config.update({ mode });
      this._modeChange$.next(mode);
    }
  }

  public setClass(c: string): void {
    this._classChange$.next(c);
  }

  public setCollapsible(collapsible: boolean): void {
    this._config.update({ collapsible });
    if (!collapsible && this.mode === 'collapsed') {
      this.setMode('standart');
    }
  }

  private _setConfig(config: ISnLayoutAppbarConfig): void {
    this.setMode(config.mode);
    this.toggleVisibility(!config.hidden);

    this.search.toggleActivation(config.search);
    this.navButton.toggleVisibility(!config.hideNavButton);
  }

  private _watchRouter(): void {
    this._routerHelper.routeChange$
      .pipe(
        startWith(this._activatedRoute),
        mapToRouteDataItem<ISnLayoutAppbarConfig>('snLayoutAppbar', true),
        takeUntil(this._destroy$),
      )
      .subscribe((config) => {
        this._config.restore();
        this._config.update(config);

        this._setConfig(this._config);
      });
  }
}
